const API_BASE: string = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(/\/$/, "");

function authHeaders(): Record<string, string> {
    const token = localStorage.getItem("token");
    return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

async function handleResponse<T>(res: Response): Promise<T> {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.message || `Request failed: ${res.status}`);
    return data as T;
}

export type EventRsvp = "going" | "interested" | "waitlisted" | null;
export type DiscussionMode = "open" | "attendees" | "announcements" | "locked";

export type SpaceEvent = {
    id: string;
    space: { id: string; name: string; accentColor?: string };
    creator: { userId: string; username: string; displayName?: string; avatarUrl?: string };
    title: string;
    description: string;
    type: string;
    tags?: string[];
    startsAt: string;
    endsAt: string;
    lifecycle: "scheduled" | "live" | "ended" | "cancelled";
    capacity: number;
    remainingCapacity: number | null;
    locationMode?: "online" | "physical";
    location: string;
    address?: string;
    counts: { going: number; interested: number; maybe: number; waitlisted: number; total: number };
    myRsvp: EventRsvp;
    reminderMinutes: number[];
    checkedInAt?: string | null;
    isCancelled: boolean;
    isPast?: boolean;
    status?: "upcoming" | "live" | "ended" | "cancelled";
    discussion: { mode: DiscussionMode; opensAt?: string | null; locksAt?: string | null; slowModeSeconds: number; isOpen: boolean };
    canDiscuss?: boolean;
    canManageDiscussion?: boolean;
    canEdit?: boolean;
    canInspect?: boolean;
    canCancel?: boolean;
    canDelete?: boolean;
    canRequestAction?: boolean;
    pendingAuthorityAction?: "cancel" | "delete" | "";
    authorityRequests?: Array<{ id: string; action: "cancel" | "delete"; status: "pending" | "approved" | "rejected"; requestedByRole: string; requestedAt: string; resolvedAt?: string | null }>;
    createdAt: string;
    updatedAt: string;
};

export type EventAttendee = {
    id: string;
    username: string;
    displayName?: string;
    avatarUrl?: string;
    status: Exclude<EventRsvp, null>;
    checkedInAt?: string | null;
};

export type EventDiscussionPost = {
    id: string;
    content: string;
    kind: "discussion" | "announcement";
    parentId?: string | null;
    pinned: boolean;
    author: { username: string; displayName?: string; avatarUrl?: string };
    canDelete: boolean;
    canPin: boolean;
    canSeeDislikes?: boolean;
    reactionCounts?: { like: number; love: number; dislike: number; total: number };
    myReaction?: "like" | "love" | "dislike" | null;
    createdAt: string;
    updatedAt: string;
};

export type EventInput = {
    spaceId?: string;
    title?: string;
    description?: string;
    type?: string;
    startsAt?: string;
    endsAt?: string;
    capacity?: number;
    locationMode?: "online" | "physical";
    location?: string;
    address?: string;
    discussion?: { mode?: DiscussionMode; opensAt?: string | null; locksAt?: string | null; slowModeSeconds?: number };
};

export async function fetchEvents(spaceId: string, page = 1, limit = 20) {
    const url = new URL(`${API_BASE}/api/events`);
    url.searchParams.set("spaceId", spaceId);
    url.searchParams.set("page", String(page));
    url.searchParams.set("limit", String(limit));
    const data = await handleResponse<any>(await fetch(url.toString(), { headers: authHeaders() }));
    return {
        events: (data.events || []) as SpaceEvent[],
        lifecycleCounts: data.lifecycleCounts || { live: 0, scheduled: 0, ended: 0, cancelled: 0 },
        serverNow: data.serverNow || null,
        total: data.total || 0,
        page: data.page || page,
        hasMore: Boolean(data.hasMore),
    };
}

export async function fetchEvent(eventId: string): Promise<SpaceEvent> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/events/${eventId}`, { headers: authHeaders() }));
    return data.event;
}

export async function createEvent(input: EventInput & { spaceId: string; title: string; startsAt: string }): Promise<SpaceEvent> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/events`, { method: "POST", headers: authHeaders(), body: JSON.stringify(input) }));
    return data.event;
}

export async function updateEvent(eventId: string, input: EventInput): Promise<SpaceEvent> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/events/${eventId}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(input) }));
    if (input.discussion) return updateEventSettings(eventId, input.discussion);
    return data.event;
}

export async function updateEventRsvp(eventId: string, status: "going" | "interested" | "none", reminderMinutes?: number[]): Promise<SpaceEvent> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/events/${eventId}/rsvp`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ status, reminderMinutes }) }));
    return data.event;
}

export async function fetchEventAttendees(eventId: string): Promise<EventAttendee[]> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/events/${eventId}/attendees`, { headers: authHeaders() }));
    return data.attendees || [];
}

export async function fetchEventDiscussion(eventId: string): Promise<{ posts: EventDiscussionPost[]; discussion: SpaceEvent["discussion"] }> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/events/${eventId}/discussion`, { headers: authHeaders() }));
    return { posts: data.posts || [], discussion: data.discussion };
}

export async function createEventDiscussionPost(eventId: string, input: { content: string; kind?: "discussion" | "announcement"; parentId?: string }): Promise<EventDiscussionPost> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/events/${eventId}/discussion`, { method: "POST", headers: authHeaders(), body: JSON.stringify(input) }));
    return data.post;
}

export async function setEventDiscussionPin(eventId: string, postId: string, pinned: boolean): Promise<EventDiscussionPost> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/events/${eventId}/discussion/${postId}/pin`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ pinned }) }));
    return data.post;
}

export async function deleteEventDiscussionPost(eventId: string, postId: string): Promise<void> {
    await handleResponse(await fetch(`${API_BASE}/api/events/${eventId}/discussion/${postId}`, { method: "DELETE", headers: authHeaders() }));
}

export async function updateEventDiscussionReaction(eventId: string, postId: string, input: { type?: "like" | "love" | "dislike"; active?: boolean }): Promise<EventDiscussionPost> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/events/${eventId}/discussion/${postId}/reaction`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(input) }));
    return data.post;
}

export async function updateEventSettings(eventId: string, input: { mode?: DiscussionMode; opensAt?: string | null; locksAt?: string | null; slowModeSeconds?: number }): Promise<SpaceEvent> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/events/${eventId}/settings`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(input) }));
    return data.event;
}

export async function checkInEvent(eventId: string): Promise<SpaceEvent> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/events/${eventId}/check-in`, { method: "PATCH", headers: authHeaders() }));
    return data.event;
}

export async function cancelEvent(eventId: string): Promise<{ event: SpaceEvent; requested?: boolean; message?: string }> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/events/${eventId}/cancel`, { method: "PATCH", headers: authHeaders() }));
    return { event: data.event, requested: Boolean(data.requested), message: data.message };
}

export async function deleteEvent(eventId: string): Promise<{ deleted: boolean; id?: string; event?: SpaceEvent; requested?: boolean; message?: string }> {
    const data = await handleResponse<any>(await fetch(`${API_BASE}/api/events/${eventId}`, { method: "DELETE", headers: authHeaders() }));
    return { deleted: Boolean(data.deleted), id: data.id || eventId, event: data.event, requested: Boolean(data.requested), message: data.message };
}
